import {
  Flex,
  LightMode,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { FileLink } from '@texas/api/endpoints/filesApi';
import { Role } from '@texas/api/endpoints/userApi';
import { variantsApi } from '@texas/api/endpoints/variantsApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useFileUploads } from '@texas/api/hooks/useFileUploads';
import { clientEndpoints } from '@texas/clientEndpoints';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { fileEvents } from '@texas/components/shared/dropzone/events';
import { FileDropzone } from '@texas/components/shared/dropzone/FileDropzone';
import { ImageDropzone } from '@texas/components/shared/dropzone/ImageDropzone';
import { maxSize } from '@texas/components/shared/dropzone/shared';
import { ErrorsList } from '@texas/components/shared/ErrorsList';
import { FileBrowserModal } from '@texas/components/shared/files/FileBrowserModal';
import { useActiveSession } from '@texas/hooks/useSession';
import { ServerError } from '@texas/types';
import { hasRole } from '@texas/utils/helpers/roleHelpers';
import {
  acceptedImageFormats,
  imageExtensions,
} from '@texas/utils/helpers/filesHelper';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CaptureSessionType } from '@texas/api/endpoints/capture/captureApi';

interface Props {
  variantId: number;
  cellX: number;
  cellY: number;
  folderId: number;
  onPhotoFetched?: (fileId: number | null) => void;
}

export function MatrixCellPhoto({
  variantId,
  cellX,
  cellY,
  folderId,
  onPhotoFetched,
}: Props) {
  const { t } = useTranslation();
  const toast = useToast();

  const session = useActiveSession();
  const allowEdit = hasRole(session.currentUser.role, [
    Role.InternalAdministrator,
    Role.InternalAgent,
    Role.SystemAdministrator,
  ]);

  const { request: updateRequest } = useApiRequest(variantsApi.updatePhoto);

  const { data: photoInfo, refetch: refetchPhoto } = useApiResource(
    variantsApi.getCellPhoto,
  );

  useEffect(() => {
    refetchPhoto(variantId, cellX, cellY);
  }, [refetchPhoto, variantId, cellX, cellY]);

  useEffect(() => {
    onPhotoFetched?.(photoInfo?.id ?? null);
  }, [photoInfo, onPhotoFetched]);

  const {
    isOpen: isRemoveFileOpen,
    onClose: onRemoveFileClose,
    onOpen: onRemoveFileOpen,
  } = useDisclosure();

  const {
    isOpen: isFileBrowserOpen,
    onClose: onFileBrowserClose,
    onOpen: onFileBrowserOpen,
  } = useDisclosure();

  const updateFile = useCallback(
    (file: FileLink) => {
      request(
        updateRequest,
        [variantId, file.id, cellX, cellY],
        (_) => {
          toast({
            title: t('fileBrowser.updatedFile'),
            status: 'success',
            isClosable: true,
          });
          refetchPhoto(variantId, cellX, cellY);
          fileEvents.matrixFileChanged.dispatch({
            matrixNodeValueXId: cellX,
            matrixNodeValueYId: cellY,
            file,
          });
        },
        (error: ServerError) => {
          toast({
            title: t('fileBrowser.updateFileFailed'),
            description: <ErrorsList errors={error.errors} />,
            status: 'error',
            isClosable: true,
          });
        },
      );
    },
    [updateRequest, variantId, cellX, cellY, toast, t, refetchPhoto],
  );

  const {
    fileUploads,
    isUploading,
    uploadFailed,
    uploadFiles,
    removeFileUpload,
  } = useFileUploads(updateFile, maxSize);

  const cellPhoto =
    photoInfo?.id && photoInfo.identifier
      ? {
          id: photoInfo.id,
          identifier: photoInfo.identifier,
          archived: photoInfo.archived,
        }
      : null;

  return (
    <LightMode>
      <Flex direction="column" gap={2} width="fit-content" color="black">
        <Text variant="header-medium">{t('variant.photo')}</Text>
        {cellPhoto && (
          <ImageDropzone
            imageId={cellPhoto.id}
            imageIdentifier={cellPhoto.identifier}
            imageSrc={clientEndpoints.previewImage(cellPhoto.identifier, 250)}
            imageArchived={cellPhoto.archived}
            allowEdit={allowEdit}
            onUpload={(files) => uploadFiles(files, folderId)}
            isUploading={isUploading}
            uploadFailed={uploadFailed}
            fileUploads={fileUploads}
            removeFileUpload={removeFileUpload}
            onRemoveFileOpen={onRemoveFileOpen}
            onFileBrowserOpen={onFileBrowserOpen}
            capturePhotoProps={{
              referenceId: variantId,
              referenceType: CaptureSessionType.ArticleVariantCellPhoto,
              text1: t('variant.photo'),
              onPhotoReceived: (file) => updateFile(file),
            }}
          />
        )}
        {allowEdit && !cellPhoto && (
          <FileDropzone
            accept={acceptedImageFormats}
            isUploading={isUploading}
            uploadFailed={uploadFailed}
            fileUploads={fileUploads}
            removeFileUpload={removeFileUpload}
            onUpload={(files) => uploadFiles(files, folderId)}
            onFileBrowserOpen={onFileBrowserOpen}
            capturePhotoProps={{
              referenceId: variantId,
              referenceType: CaptureSessionType.ArticleVariantCellPhoto,
              text1: t('variant.photo'),
              onPhotoReceived: (file) => updateFile(file),
            }}
          />
        )}
        <VerifyDialogWithRequest
          headerTitle={t('fileBrowser.removeFile')}
          secondaryButtonTitle={t('general.cancel')}
          primaryButtonTitle={t('general.confirm')}
          primaryRequest={variantsApi.deletePhoto}
          args={[variantId, cellX, cellY]}
          isOpen={isRemoveFileOpen}
          onClose={onRemoveFileClose}
          onPerformed={() => {
            refetchPhoto(variantId, cellX, cellY);
            fileEvents.matrixFileChanged.dispatch({
              matrixNodeValueXId: cellX,
              matrixNodeValueYId: cellY,
              file: null,
            });
          }}
          onSuccessTitle={t('fileBrowser.fileRemoved')}
        >
          {t('alert.areYouSure')}
        </VerifyDialogWithRequest>
        <FileBrowserModal
          rootFolderId={folderId}
          mode="Select"
          isOpen={isFileBrowserOpen}
          onClose={onFileBrowserClose}
          extensionFilter={imageExtensions}
          onSelect={(file: FileLink) => {
            updateFile(file);
            onFileBrowserClose();
          }}
        />
      </Flex>
    </LightMode>
  );
}
